// Bloque «list»: tabla (en el celular, una tarjeta por registro) o tarjetas.

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { ProjectedRecord } from "@/convex/lib/sites/resolve";
import { cn } from "@/lib/utils";
import { formatSiteValue, isEmptyValue, type FieldMeta, type SiteBlockOf } from "../values";
import { BlockEmpty, BlockTitle } from "./shared";

const EMPTY_TEXT = "Todavía no hay nada para mostrar.";

/**
 * Registros por tanda: el servidor manda hasta `SITE_MAX_RECORDS` y pintar
 * todos de una alarga la página. Las tarjetas van de 12 (cabe en 1, 2 o 3
 * columnas).
 */
export const LIST_PAGE_SIZE = { table: 20, cards: 12 } as const;

/**
 * Cuántos registros se ven y el «Ver más». Vuelve a la primera tanda cuando
 * cambian los registros (otro hijo elegido con `picks`), no cuando solo
 * cambia un valor.
 */
export function useListBatches(records: ProjectedRecord[], pageSize: number) {
  // El tamaño entra en la identidad: pasar de tabla a tarjetas en el editor
  // también vuelve a la primera tanda.
  const identity = `${pageSize}:${records.map((r) => r.id).join("|")}`;
  const [state, setState] = useState({ identity, shown: pageSize });
  const shown = state.identity === identity ? state.shown : pageSize;
  const visible = records.length > shown ? records.slice(0, shown) : records;
  return {
    visible,
    remaining: records.length - visible.length,
    more: () => setState({ identity, shown: shown + pageSize }),
  };
}

function MoreButton({ remaining, onClick }: { remaining: number; onClick: () => void }) {
  if (remaining <= 0) return null;
  return (
    <div className="flex justify-center">
      <button
        type="button"
        onClick={onClick}
        data-testid="site-list-more"
        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-border bg-card px-4 text-sm font-medium text-foreground shadow-xs outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/30 @min-[40rem]:w-auto @min-[40rem]:px-6"
      >
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        Ver más ({remaining} {remaining === 1 ? "restante" : "restantes"})
      </button>
    </div>
  );
}

/** Valor de una celda; las opciones de lista van como etiqueta. */
function Value({ record, field }: { record: ProjectedRecord; field: FieldMeta }) {
  const value = record.values[field.key];
  const text = formatSiteValue(value, field.type);
  if (field.type === "select" && !isEmptyValue(value)) {
    return (
      <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
        {text}
      </span>
    );
  }
  return <span className={cn(isEmptyValue(value) && "text-muted-foreground")}>{text}</span>;
}

/**
 * Tarjeta de un registro: el primer campo con valor como título (uno vacío
 * dejaba «—» de título) y el resto como pares.
 */
function RecordCard({ record, fields, className }: { record: ProjectedRecord; fields: FieldMeta[]; className?: string }) {
  const first = fields.find((f) => !isEmptyValue(record.values[f.key])) ?? fields[0];
  const rest = fields.filter((f) => f !== first);
  return (
    <li className={cn("rounded-lg border border-border bg-card p-4 text-card-foreground", className)} data-testid="site-record-card">
      {first && (
        <p className="font-medium text-foreground">
          <Value record={record} field={first} />
        </p>
      )}
      {rest.length > 0 && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          {rest.map((f) => (
            <div key={f.key} className="contents">
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd className="min-w-0 break-words text-right text-foreground">
                <Value record={record} field={f} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

export function ListBlock({ block }: { block: SiteBlockOf<"list"> }) {
  const { fields } = block;
  const { visible: records, remaining, more } = useListBatches(block.records, LIST_PAGE_SIZE[block.variant] ?? LIST_PAGE_SIZE.table);
  return (
    <section className="space-y-3" aria-label={block.title}>
      {block.title && <BlockTitle>{block.title}</BlockTitle>}
      {records.length === 0 ? (
        <BlockEmpty>{block.empty || EMPTY_TEXT}</BlockEmpty>
      ) : block.variant === "cards" ? (
        <ul className="grid gap-3 @min-[40rem]:grid-cols-2 @min-[64rem]:grid-cols-3">
          {records.map((r) => (
            <RecordCard key={r.id} record={r} fields={fields} />
          ))}
        </ul>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card @min-[48rem]:block" data-testid="site-list-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/60 text-left">
                  {fields.map((f) => (
                    <th key={f.key} scope="col" className="px-4 py-2.5 font-medium text-muted-foreground">
                      {f.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    {fields.map((f) => (
                      <td key={f.key} className="px-4 py-2.5 align-top text-foreground">
                        <Value record={r} field={f} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="space-y-2 @min-[48rem]:hidden" data-testid="site-list-stacked">
            {records.map((r) => (
              <RecordCard key={r.id} record={r} fields={fields} />
            ))}
          </ul>
        </>
      )}
      <MoreButton remaining={remaining} onClick={more} />
    </section>
  );
}
